<script>
import debounce from 'lodash/debounce';
import Multiselect from 'vue-multiselect';
import _map from 'lodash/map';
import entityDataParsers from '@mixins/entityDataParsers';

export default {
  components: {
    Multiselect,
  },
  mixins: [entityDataParsers],
  props: {
    // eslint-disable-next-line
    value: {
      required: true,
    },
    initialValue: {
      default: null,
      type: Object,
    },
    reference: {
      default: '',
      type: String,
    },
    placeholder: {
      default: 'Type to search',
      type: String,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    state: {
      default: false,
      type: Boolean,
    },
    model: {
      required: true,
      type: Function,
    },
    sort: {
      type: String,
      default: 'name',
    },
    provider: {
      type: Function,
      default: null,
    },
    params: {
      type: Object,
      default: null,
    },
    where: {
      type: [Array, String],
      default() {
        return [];
      },
    },
    select: {
      type: Array,
      default() {
        return ['id', 'name'];
      },
    },
    dataParseFn: {
      type: String,
      required: false,
      default: null,
    },
    include: {
      type: Array,
      default() {
        return [];
      },
      required: false,
    },
    clearOnChange: {
      required: false,
      type: Boolean,
      default() {
        return false;
      },
    },
    label: {
      default: 'name',
      type: String,
    },    
  },
  data() {
    return {
      entities: [],
      isLoading: false,
      selectedEntity: this.initialValue,
      focused: false,
      minChars: 2,
      unique: Math.random().toString(36).substr(2, 9),
    };
  },
  watch: {
    selectedEntity(val) {
      // FOR SST, REPORTS CAN ONLY BE SELECTED IF COMPLETED & LOCKED
      if(this.reference === 'SST' && this.model.name === 'Report'
          && val && val.id != null && (!val.completed || !val.locked)) {
        this.entities = [];
        this.selectedEntity = null;
        this.value = null;
        this.$toasted.error('Report is not completed and/or locked');
      } else {
        const id = val ? val.id : null;
        this.$emit('input', id);
        this.$emit('change', id);
        this.$emit('change-val', val);
        this.entities = [];

        if (val && this.clearOnChange) {
          this.selectedEntity = null;
        }
      }
    },
    initialValue: {
      handler() {
        this.selectedEntity = this.initialValue;
      },
      deep: true,
      immediate: false,
    },
  },
  methods: {
    clear() {
      this.initialValue = {};
      this.selectedEntity = null;
      this.value = null;
    },
    getProvider: debounce(function (query) {
      if (query.length >= 3) {
        this.isLoading = true;
        if (this.provider) {
          this.provider(query)
            .then((response) => {
              this.entities = response.data;
              this.isLoading = false;
            })
            .catch(() => {
              this.isLoading = false;
            });
        } else {
          this.asyncEntities(query);
        }
      }
    }, 300),
    asyncEntities: debounce(function (query) {
      if (query.length >= this.minChars) {
        this.isLoading = true;
        let modelQuery = this.model;

        // check for additional url params
        if (this.params) {
          modelQuery = modelQuery.params(this.params);
        }

        // if prop select is passed as empty array, select all
        if (this.select.length > 0) {
          modelQuery = modelQuery.select(this.select);
        }

        // check where param
        if (this.where !== null && Array.isArray(this.where) && this.where.length > 0) {
          this.where.forEach((el) => {
            if (typeof el === 'object' && el !== null && 'name' in el && 'value' in el) {
              modelQuery = modelQuery.where(el.name, el.value);
            } else if (typeof el === 'object' && el !== null && 'name' in el && !('value' in el)) {
              modelQuery = modelQuery.where(el.name, encodeURIComponent(query));
            }
          });
        } else if(this.where !== null && typeof this.where === 'string' && this.where.length > 0) {
          modelQuery = modelQuery.where(this.where, encodeURIComponent(query));
        } else {
          modelQuery = modelQuery.where('name', encodeURIComponent(query));
        }

        // check include param
        if (this.include.length > 0) {
          modelQuery = modelQuery.include(this.include);
        }

        modelQuery
          .orderBy(`-${this.sort}`)
          .page(1)
          .limit(30)
          .get()
          .then((response) => {
            if (this.dataParseFn != null) {
              this.entities = _map(response.data, (item) => this.parsers[this.dataParseFn](item));
            } else {
              this.entities = response.data;
            }

            this.isLoading = false;
          })
          .catch(() => {
            this.isLoading = false;
          });
      }
    }, 300),
    open() {
      // fix for unselecting
      this.focused = true;
      if (this.selectedEntity != null && 'name' in this.selectedEntity) {
        this.$refs[`multiselect${this.unique}`].search = this.selectedEntity.name;
      }
    },
  },
};
</script>

<template>
  <div class="multiselect-container kks-multiselect-container" :class="{ 'is-invalid': state, focused: focused }">
    <Multiselect
      :ref="`multiselect${unique}`"
      :key="`multiselect-${unique}`"
      v-model="selectedEntity"
      :label="label"
      track-by="id"
      :placeholder="placeholder"
      open-direction="bottom"
      :options="entities"
      :searchable="true"
      :loading="isLoading"
      :internal-search="false"
      :options-limit="30"
      :max-height="600"
      :disabled="disabled"
      :show-no-results="true"
      @open="open"
      @close="focused = false"
      @search-change="getProvider"
    >
      <div slot="noResult" class="text-center">
        {{ $t('messages.empty_list') }}
        <br />
        {{ $t('messages.multiselect_enter') }}
      </div>
      <div slot="noOptions" class="text-center">
        {{ $t('messages.empty_list') }}
        <br />
        {{ $t('messages.multiselect_enter') }}
      </div>
    </Multiselect>
  </div>
</template>
